import { useState, useEffect } from 'react'
import { useTranslation } from '../../i18n'
import { useTheme } from '../../hooks/useTheme'
import { userApi } from '../../api'
import { Button } from '../ui'
import type { UserRelation } from '../../types/api'

interface EditVirtualTraineeModalProps {
  user: UserRelation
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

/**
 * Full-screen sheet for editing a virtual trainee — same chrome as
 * ActivateProgramSheet / AssignProgramSheet and the same form language as CreateVirtualTrainee.
 */
export function EditVirtualTraineeModal({
  user,
  isOpen,
  onClose,
  onSuccess,
}: EditVirtualTraineeModalProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [firstName, setFirstName] = useState(user?.firstName || '')
  const [lastName, setLastName] = useState(user?.lastName || '')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setFirstName(user?.firstName || '')
      setLastName(user?.lastName || '')
      setError(null)
    }
  }, [isOpen, user])

  const handleSave = async () => {
    if (!firstName.trim()) {
      setError(t.virtualTrainee.errors.invalidData)
      return
    }

    if (firstName.trim().length < 2 || firstName.trim().length > 30) {
      setError(t.virtualTrainee.errors.invalidData)
      return
    }

    if (lastName.trim().length > 30) {
      setError(t.virtualTrainee.errors.invalidData)
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      await userApi.updateVirtualTrainee(user.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
      })

      if (onSuccess) {
        onSuccess()
      } else {
        window.location.reload()
      }
    } catch (err) {
      console.error('Error updating virtual trainee:', err)
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError(t.virtualTrainee.errors.unknown)
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) {
    return null
  }

  const inputStyle = {
    backgroundColor: theme.inputBg,
    color: theme.text.primary,
    border: `1px solid ${theme.inputBorder}`,
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          className="p-2 -ml-2 rounded-xl disabled:opacity-40"
          style={{ color: theme.text.primary }}
          aria-label={t.userDetail.edit.cancel}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
          {t.userDetail.edit.title}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6">
        {error && (
          <div
            className="rounded-xl p-4 flex items-start gap-3 mb-5"
            style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-sm flex-1" style={{ color: theme.text.primary }}>{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-xl leading-none active:opacity-75 flex-shrink-0"
              style={{ color: theme.text.muted }}
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="edit-first-name" className="text-sm font-medium" style={{ color: theme.text.primary }}>
              {t.virtualTrainee.form.firstNameRequired}
            </label>
            <input
              id="edit-first-name"
              type="text"
              value={firstName}
              onChange={e => {
                setFirstName(e.target.value)
                setError(null)
              }}
              placeholder={t.virtualTrainee.form.firstNamePlaceholder}
              disabled={isSaving}
              minLength={2}
              maxLength={30}
              pattern="[\p{L}\p{M}\p{N}\s'-]+"
              required
              className="w-full rounded-xl px-4 py-3 text-base outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={inputStyle}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="edit-last-name" className="text-sm font-medium" style={{ color: theme.text.primary }}>
              {t.virtualTrainee.form.lastName}
            </label>
            <input
              id="edit-last-name"
              type="text"
              value={lastName}
              onChange={e => {
                setLastName(e.target.value)
                setError(null)
              }}
              placeholder={t.virtualTrainee.form.lastNamePlaceholder}
              disabled={isSaving}
              maxLength={30}
              pattern="[\p{L}\p{M}\p{N}\s'-]*"
              className="w-full rounded-xl px-4 py-3 text-base outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={inputStyle}
            />
            <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
              {t.virtualTrainee.form.lastNameHelp}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleSave}
          disabled={isSaving || !firstName.trim()}
        >
          {isSaving ? t.userDetail.edit.saving : t.userDetail.edit.save}
        </Button>
      </div>
    </div>
  )
}
